import { z } from 'zod';

import i18n from '~/common/helpers/i18n';

export function isJsonLikeString(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

export function isValidJsonString(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

const invalidJsonMessage = () =>
  i18n.t('scenes.monitor.agentLogs.collect.invalidJson') as string;

const requiredVariableMessage = () =>
  i18n.t('scenes.monitor.agentLogs.collect.requiredVariable') as string;

export const jsonCompatibleStringSchema = z
  .string()
  .superRefine((value, ctx) => {
    const trimmed = value.trim();
    if (!trimmed || !isJsonLikeString(trimmed)) {
      return;
    }

    if (!isValidJsonString(trimmed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: invalidJsonMessage()
      });
    }
  });

export const requiredVariableValueSchema = z
  .string()
  .superRefine((value, ctx) => {
    const trimmed = value.trim();
    if (!trimmed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredVariableMessage()
      });
      return;
    }

    if (!isJsonLikeString(trimmed) || isValidJsonString(trimmed)) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: invalidJsonMessage()
    });
  });

export const testCaseEditorSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, {
      message: i18n.t(
        'scenes.build.execution.experiments.save.empty_experiment_name'
      ) as string
    }),
  variables: z.record(z.string(), requiredVariableValueSchema),
  expectedResults: z.record(z.string(), jsonCompatibleStringSchema)
});

export type TestCaseEditorFormValues = z.infer<typeof testCaseEditorSchema>;

export function isTestCaseEditorValid(
  values: TestCaseEditorFormValues
): boolean {
  return testCaseEditorSchema.safeParse(values).success;
}

export function parseExpectedResultText(
  value: string
): Record<string, unknown> | string | number | boolean | unknown[] | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (isJsonLikeString(trimmed) && isValidJsonString(trimmed)) {
    return JSON.parse(trimmed) as
      Record<string, unknown> | string | number | boolean | unknown[];
  }

  return trimmed;
}

export function toCollectExpectedResults(
  expectedResults: Record<string, string>
): Array<{ evalId: string; expectedResult: Record<string, unknown> }> {
  return Object.entries(expectedResults)
    .map(([evalId, raw]) => {
      const parsed = parseExpectedResultText(raw);
      if (parsed === null) {
        return null;
      }

      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        return {
          evalId,
          expectedResult: parsed as Record<string, unknown>
        };
      }

      return {
        evalId,
        expectedResult: { value: parsed }
      };
    })
    .filter(
      (
        item
      ): item is { evalId: string; expectedResult: Record<string, unknown> } =>
        item !== null
    );
}
